'use client';

import React, { useState } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { Plus, Save, Trash2, GripVertical, ChevronDown, ChevronUp, Star, AlignLeft, List, Target, BarChart2, Brain, CheckSquare, Circle, GitBranch, Edit2, Copy, Check, AlertCircle, X,  } from 'lucide-react';
import type { AppraisalTemplate, TemplateSection, Question, QuestionType } from '@/store/appraisalStore';

interface Props {
  template: AppraisalTemplate;
  onUpdate: (template: AppraisalTemplate) => void;
  onToast: (type: 'success' | 'error' | 'warning' | 'info', message: string) => void;
}

const QUESTION_TYPES: { type: QuestionType; label: string; icon: React.ReactNode }[] = [
  { type: 'rating', label: 'Rating', icon: <Star size={15} /> },
  { type: 'text', label: 'Short Text', icon: <AlignLeft size={15} /> },
  { type: 'textarea', label: 'Paragraph', icon: <AlignLeft size={15} /> },
  { type: 'single-select', label: 'Single Select', icon: <Circle size={15} /> },
  { type: 'multi-select', label: 'Multi Select', icon: <CheckSquare size={15} /> },
  { type: 'dropdown', label: 'Dropdown', icon: <List size={15} /> },
  { type: 'goal', label: 'Goal Based', icon: <Target size={15} /> },
  { type: 'kpi', label: 'KPI Based', icon: <BarChart2 size={15} /> },
  { type: 'competency', label: 'Competency', icon: <Brain size={15} /> },
  { type: 'radio', label: 'Radio', icon: <Circle size={15} /> },
  { type: 'checkbox', label: 'Checkbox', icon: <CheckSquare size={15} /> },
  { type: 'conditional', label: 'Conditional', icon: <GitBranch size={15} /> },
];

const TYPE_ICON_CLASS: Record<QuestionType, string> = {
  rating: 'pn-appraisal__q-item-type-icon--rating',
  text: 'pn-appraisal__q-item-type-icon--text',
  textarea: 'pn-appraisal__q-item-type-icon--text',
  'single-select': 'pn-appraisal__q-item-type-icon--select',
  'multi-select': 'pn-appraisal__q-item-type-icon--select',
  dropdown: 'pn-appraisal__q-item-type-icon--select',
  goal: 'pn-appraisal__q-item-type-icon--goal',
  kpi: 'pn-appraisal__q-item-type-icon--kpi',
  competency: 'pn-appraisal__q-item-type-icon--competency',
  radio: 'pn-appraisal__q-item-type-icon--select',
  checkbox: 'pn-appraisal__q-item-type-icon--select',
  conditional: 'pn-appraisal__q-item-type-icon--text',
};

function newQuestion(sectionId: string, order: number): Question {
  return {
    id: `q-${Date.now()}`,
    text: 'New question',
    type: 'rating',
    required: true,
    weightage: 0,
    minRating: 1,
    maxRating: 5,
    sectionId,
    order,
  };
}

function newSection(order: number): TemplateSection {
  return {
    id: `sec-${Date.now()}`,
    name: 'New Section',
    description: 'Add a description for this section',
    order,
    weightage: 0,
    questions: [],
  };
}

export default function TemplateBuilderCanvas({ template, onUpdate, onToast }: Props) {
  const [editingName, setEditingName] = useState(false);
  const [nameVal, setNameVal] = useState(template.name);
  const [descVal, setDescVal] = useState(template.description);
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());
  const [activeQuestionId, setActiveQuestionId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const totalQuestions = template.sections.reduce((acc, s) => acc + s.questions.length, 0);

  const update = (sections: TemplateSection[]) => {
    onUpdate({ ...template, sections, updatedAt: new Date().toISOString() });
  };

  const handleSave = async () => {
    setSaving(true);
    await new Promise((r) => setTimeout(r, 500)); // Backend integration point
    setSaving(false);
    onToast('success', `Template "${template.name}" saved successfully`);
  };

  const handleNameSave = () => {
    onUpdate({ ...template, name: nameVal, description: descVal, updatedAt: new Date().toISOString() });
    setEditingName(false);
    onToast('success', 'Template details updated');
  };

  const handleAddSection = () => {
    const sec = newSection(template.sections.length + 1);
    update([...template.sections, sec]);
    onToast('info', 'New section added — add questions below');
  };

  const handleDeleteSection = (sectionId: string) => {
    update(template.sections.filter((s) => s.id !== sectionId));
    onToast('success', 'Section removed');
  };

  const handleDuplicateSection = (section: TemplateSection) => {
    const duped: TemplateSection = {
      ...section,
      id: `sec-${Date.now()}`,
      name: `${section.name} (Copy)`,
      questions: section.questions.map((q) => ({ ...q, id: `q-${Date.now()}-${q.id}`, sectionId: `sec-${Date.now()}` })),
    };
    update([...template.sections, duped]);
    onToast('success', `Section "${section.name}" duplicated`);
  };

  const handleAddQuestion = (sectionId: string) => {
    const section = template.sections.find((s) => s.id === sectionId);
    if (!section) return;
    const q = newQuestion(sectionId, section.questions.length + 1);
    const newSections = template.sections.map((s) =>
      s.id === sectionId ? { ...s, questions: [...s.questions, q] } : s
    );
    update(newSections);
    setActiveQuestionId(q.id);
  };

  const handleDeleteQuestion = (sectionId: string, questionId: string) => {
    const newSections = template.sections.map((s) =>
      s.id === sectionId ? { ...s, questions: s.questions.filter((q) => q.id !== questionId) } : s
    );
    update(newSections);
    if (activeQuestionId === questionId) setActiveQuestionId(null);
    onToast('success', 'Question removed');
  };

  const handleUpdateQuestion = (sectionId: string, updated: Question) => {
    const newSections = template.sections.map((s) =>
      s.id === sectionId
        ? { ...s, questions: s.questions.map((q) => (q.id === updated.id ? updated : q)) }
        : s
    );
    update(newSections);
  };

  const handleUpdateSection = (updated: TemplateSection) => {
    update(template.sections.map((s) => (s.id === updated.id ? updated : s)));
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const { source, destination, type } = result;

    if (type === 'SECTION') {
      const reordered = Array.from(template.sections);
      const [removed] = reordered.splice(source.index, 1);
      reordered.splice(destination.index, 0, removed);
      update(reordered.map((s, i) => ({ ...s, order: i + 1 })));
      return;
    }

    if (type === 'QUESTION') {
      const sectionId = source.droppableId.replace('questions-', '');
      const section = template.sections.find((s) => s.id === sectionId);
      if (!section) return;
      const reordered = Array.from(section.questions);
      const [removed] = reordered.splice(source.index, 1);
      reordered.splice(destination.index, 0, removed);
      update(
        template.sections.map((s) =>
          s.id === sectionId ? { ...s, questions: reordered.map((q, i) => ({ ...q, order: i + 1 })) } : s
        )
      );
    }
  };

  const toggleSection = (sectionId: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
  };

  const activeQuestion = template.sections
    .flatMap((s) => s.questions)
    .find((q) => q.id === activeQuestionId);
  const activeSectionId = template.sections.find((s) => s.questions.some((q) => q.id === activeQuestionId))?.id;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Canvas Header */}
      <div className="pn-appraisal__question-builder-canvas-header">
        <div style={{ flex: 1, minWidth: 0 }}>
          {editingName ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <input
                className="pn-appraisal__form-input"
                value={nameVal}
                onChange={(e) => setNameVal(e.target.value)}
                style={{ fontSize: 14, fontWeight: 700, padding: '6px 10px' }}
                aria-label="Template name"
                autoFocus
              />
              <input
                className="pn-appraisal__form-input"
                value={descVal}
                onChange={(e) => setDescVal(e.target.value)}
                style={{ fontSize: 12, padding: '5px 10px' }}
                aria-label="Template description"
                placeholder="Template description…"
              />
              <div style={{ display: 'flex', gap: 6 }}>
                <button className="pn-appraisal__btn pn-appraisal__btn--primary pn-appraisal__btn--sm" onClick={handleNameSave}>
                  <Check size={12} /> Save
                </button>
                <button className="pn-appraisal__btn pn-appraisal__btn--secondary pn-appraisal__btn--sm" onClick={() => setEditingName(false)}>
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className="pn-appraisal__question-builder-canvas-header-title">{template.name}</span>
                <span style={{ fontSize: 10, fontWeight: 700, background: 'var(--pn-primary-light)', color: 'var(--pn-primary)', padding: '2px 7px', borderRadius: 4 }}>
                  {template.version}
                </span>
                <span className={`pn-appraisal__status-badge ${template.status === 'active' ? 'pn-appraisal__status-badge--active' : 'pn-appraisal__status-badge--draft'}`} style={{ fontSize: 10 }}>
                  {template.status === 'active' ? 'Active' : 'Draft'}
                </span>
              </div>
              <div style={{ fontSize: 12, color: 'var(--pn-text-muted)', marginTop: 2 }}>
                {template.sections.length} sections · {totalQuestions} questions
              </div>
            </div>
          )}
        </div>
        <div className="pn-appraisal__question-builder-canvas-header-actions">
          {!editingName && (
            <button
              className="pn-appraisal__btn pn-appraisal__btn--ghost pn-appraisal__btn--sm"
              onClick={() => setEditingName(true)}
              aria-label="Edit template name"
            >
              <Edit2 size={13} /> Edit
            </button>
          )}
          <button
            className="pn-appraisal__btn pn-appraisal__btn--secondary pn-appraisal__btn--sm"
            onClick={handleAddSection}
          >
            <Plus size={13} /> Add Section
          </button>
          <button
            className={`pn-appraisal__btn pn-appraisal__btn--primary pn-appraisal__btn--sm${saving ? ' pn-appraisal__btn--loading' : ''}`}
            onClick={handleSave}
            disabled={saving}
          >
            {!saving && <Save size={13} />}
            {saving ? 'Saving…' : 'Save Template'}
          </button>
        </div>
      </div>

      {/* Canvas Body — split: sections + active question editor */}
      <div className="pn-appraisal__question-builder-canvas-body" style={{ display: 'flex', gap: 0, flex: 1, overflow: 'hidden' }}>
        {/* Section/question list */}
        <div style={{ flex: activeQuestionId ? '0 0 55%' : '1', overflow: 'hidden', display: 'flex', flexDirection: 'column', borderRight: activeQuestionId ? '1px solid var(--pn-border)' : 'none' }}>
          <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
            {template.sections.length === 0 ? (
              <div className="pn-appraisal__empty">
                <div className="pn-appraisal__empty-icon">
                  <Plus size={26} />
                </div>
                <div className="pn-appraisal__empty-title">No sections yet</div>
                <p className="pn-appraisal__empty-desc">
                  Sections group related questions together. Add your first section to start building this template.
                </p>
                <button className="pn-appraisal__btn pn-appraisal__btn--primary pn-appraisal__btn--sm" onClick={handleAddSection}>
                  <Plus size={13} /> Add First Section
                </button>
              </div>
            ) : (
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="sections" type="SECTION">
                  {(provided) => (
                    <div ref={provided.innerRef} {...provided.droppableProps}>
                      {template.sections.map((section, sIdx) => {
                        const isCollapsed = collapsedSections.has(section.id);
                        return (
                          <Draggable key={section.id} draggableId={section.id} index={sIdx}>
                            {(dragProvided, dragSnapshot) => (
                              <div
                                ref={dragProvided.innerRef}
                                {...dragProvided.draggableProps}
                                className={`pn-appraisal__q-section${dragSnapshot.isDragging ? ' pn-appraisal__q-section--dragging' : ''}`}
                              >
                                {/* Section Header */}
                                <div className="pn-appraisal__q-section-header">
                                  <div {...dragProvided.dragHandleProps} className="pn-appraisal__q-section-header-drag" aria-label="Drag section">
                                    <GripVertical size={16} />
                                  </div>
                                  <div className="pn-appraisal__q-section-header-info" style={{ flex: 1 }}>
                                    <SectionNameEditor
                                      section={section}
                                      onUpdate={handleUpdateSection}
                                    />
                                    <div className="pn-appraisal__q-section-header-meta">
                                      {section.questions.length} questions
                                    </div>
                                  </div>
                                  <div className="pn-appraisal__q-section-header-actions">
                                    <button
                                      className="pn-appraisal__btn pn-appraisal__btn--ghost pn-appraisal__btn--icon"
                                      onClick={() => handleAddQuestion(section.id)}
                                      data-tooltip="Add question"
                                      aria-label="Add question to section"
                                      style={{ width: 28, height: 28 }}
                                    >
                                      <Plus size={13} />
                                    </button>
                                    <button
                                      className="pn-appraisal__btn pn-appraisal__btn--ghost pn-appraisal__btn--icon"
                                      onClick={() => handleDuplicateSection(section)}
                                      data-tooltip="Duplicate section"
                                      aria-label="Duplicate section"
                                      style={{ width: 28, height: 28 }}
                                    >
                                      <Copy size={13} />
                                    </button>
                                    <button
                                      className="pn-appraisal__btn pn-appraisal__btn--ghost pn-appraisal__btn--icon"
                                      onClick={() => handleDeleteSection(section.id)}
                                      data-tooltip="Delete section"
                                      aria-label="Delete section"
                                      style={{ width: 28, height: 28, color: 'var(--pn-danger)' }}
                                    >
                                      <Trash2 size={13} />
                                    </button>
                                    <button
                                      className="pn-appraisal__btn pn-appraisal__btn--ghost pn-appraisal__btn--icon"
                                      onClick={() => toggleSection(section.id)}
                                      aria-label={isCollapsed ? 'Expand section' : 'Collapse section'}
                                      style={{ width: 28, height: 28 }}
                                    >
                                      {isCollapsed ? <ChevronDown size={13} /> : <ChevronUp size={13} />}
                                    </button>
                                  </div>
                                </div>

                                {/* Section Body */}
                                {!isCollapsed && (
                                  <div className="pn-appraisal__q-section-body">
                                    <Droppable droppableId={`questions-${section.id}`} type="QUESTION">
                                      {(qProvided) => (
                                        <div ref={qProvided.innerRef} {...qProvided.droppableProps} style={{ minHeight: 8 }}>
                                          {section.questions.length === 0 ? (
                                            <div
                                              style={{
                                                border: '2px dashed var(--pn-border)',
                                                borderRadius: 'var(--pn-radius)',
                                                padding: '16px',
                                                textAlign: 'center',
                                                fontSize: 12,
                                                color: 'var(--pn-text-muted)',
                                                cursor: 'pointer',
                                              }}
                                              onClick={() => handleAddQuestion(section.id)}
                                              role="button"
                                              tabIndex={0}
                                              onKeyDown={(e) => e.key === 'Enter' && handleAddQuestion(section.id)}
                                            >
                                              Click to add a question to this section
                                            </div>
                                          ) : (
                                            section.questions.map((q, qIdx) => (
                                              <Draggable key={q.id} draggableId={q.id} index={qIdx}>
                                                {(qDragProvided, qDragSnapshot) => (
                                                  <div
                                                    ref={qDragProvided.innerRef}
                                                    {...qDragProvided.draggableProps}
                                                    className={`pn-appraisal__q-item${activeQuestionId === q.id ? ' pn-appraisal__q-item--active' : ''}${qDragSnapshot.isDragging ? ' pn-appraisal__q-item--dragging' : ''}`}
                                                    onClick={() => setActiveQuestionId(activeQuestionId === q.id ? null : q.id)}
                                                  >
                                                    <div {...qDragProvided.dragHandleProps} className="pn-appraisal__q-item-drag" aria-label="Drag question">
                                                      <GripVertical size={14} />
                                                    </div>
                                                    <div className={`pn-appraisal__q-item-type-icon ${TYPE_ICON_CLASS[q.type]}`}>
                                                      {QUESTION_TYPES.find((t) => t.type === q.type)?.icon}
                                                    </div>
                                                    <div className="pn-appraisal__q-item-content">
                                                      <div className="pn-appraisal__q-item-text">{q.text}</div>
                                                      <div className="pn-appraisal__q-item-meta">
                                                        <span className="pn-appraisal__q-item-tag pn-appraisal__q-item-tag--type">
                                                          {QUESTION_TYPES.find((t) => t.type === q.type)?.label}
                                                        </span>
                                                        <span className={`pn-appraisal__q-item-tag ${q.required ? 'pn-appraisal__q-item-tag--required' : 'pn-appraisal__q-item-tag--optional'}`}>
                                                          {q.required ? 'Required' : 'Optional'}
                                                        </span>
                                                        {q.weightage > 0 && (
                                                          <span className="pn-appraisal__q-item-tag pn-appraisal__q-item-tag--weighted">
                                                            {q.weightage}% weight
                                                          </span>
                                                        )}
                                                      </div>
                                                    </div>
                                                    <div className="pn-appraisal__q-item-actions">
                                                      <button
                                                        className="pn-appraisal__table-actions-btn"
                                                        onClick={(e) => { e.stopPropagation(); handleDeleteQuestion(section.id, q.id); }}
                                                        data-tooltip="Delete question"
                                                        aria-label="Delete question"
                                                      >
                                                        <Trash2 size={13} />
                                                      </button>
                                                    </div>
                                                  </div>
                                                )}
                                              </Draggable>
                                            ))
                                          )}
                                          {qProvided.placeholder}
                                        </div>
                                      )}
                                    </Droppable>
                                    <button
                                      className="pn-appraisal__btn pn-appraisal__btn--ghost pn-appraisal__btn--sm"
                                      onClick={() => handleAddQuestion(section.id)}
                                      style={{ marginTop: 8, width: '100%', justifyContent: 'center', borderStyle: 'dashed', border: '1px dashed var(--pn-border)' }}
                                    >
                                      <Plus size={13} /> Add Question
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}
                          </Draggable>
                        );
                      })}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            )}
          </div>
        </div>

        {/* Question Editor Panel */}
        {activeQuestion && activeSectionId && (
          <div style={{ flex: '0 0 45%', overflowY: 'auto', padding: 16 }}>
            <QuestionEditor
              question={activeQuestion}
              sectionId={activeSectionId}
              onUpdate={(updated) => handleUpdateQuestion(activeSectionId, updated)}
              onClose={() => setActiveQuestionId(null)}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// ── Section Name Editor ──────────────────────────────────────
function SectionNameEditor({ section, onUpdate }: { section: TemplateSection; onUpdate: (s: TemplateSection) => void }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(section.name);
  const [weight, setWeight] = useState(section.weightage);

  if (editing) {
    return (
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <input
          className="pn-appraisal__form-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          style={{ fontSize: 13, padding: '4px 8px', fontWeight: 600 }}
          autoFocus
          aria-label="Section name"
        />
        <button
          className="pn-appraisal__btn pn-appraisal__btn--primary pn-appraisal__btn--icon"
          style={{ width: 28, height: 28 }}
          onClick={() => { onUpdate({ ...section, name }); setEditing(false); }}
          aria-label="Save section name"
        >
          <Check size={12} />
        </button>
        <button
          className="pn-appraisal__btn pn-appraisal__btn--ghost pn-appraisal__btn--icon"
          style={{ width: 28, height: 28 }}
          onClick={() => setEditing(false)}
          aria-label="Cancel editing"
        >
          <X size={12} />
        </button>
      </div>
    );
  }

  return (
    <div
      className="pn-appraisal__q-section-header-name"
      onClick={() => setEditing(true)}
      style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
    >
      {section.name}
      <Edit2 size={11} style={{ color: 'var(--pn-text-muted)', opacity: 0.6 }} />
    </div>
  );
}

// ── Question Editor ──────────────────────────────────────────
function QuestionEditor({
  question,
  sectionId,
  onUpdate,
  onClose,
}: {
  question: Question;
  sectionId: string;
  onUpdate: (q: Question) => void;
  onClose: () => void;
}) {
  const [localQ, setLocalQ] = useState<Question>({ ...question });
  const [optionInput, setOptionInput] = useState('');

  const update = (partial: Partial<Question>) => {
    const updated = { ...localQ, ...partial };
    setLocalQ(updated);
    onUpdate(updated);
  };

  const addOption = () => {
    if (!optionInput.trim()) return;
    update({ options: [...(localQ.options || []), optionInput.trim()] });
    setOptionInput('');
  };

  const removeOption = (idx: number) => {
    update({ options: (localQ.options || []).filter((_, i) => i !== idx) });
  };

  const showOptions = ['single-select', 'multi-select', 'dropdown', 'radio', 'checkbox'].includes(localQ.type);
  const showRating = ['rating', 'competency'].includes(localQ.type);
  const showCharLimit = ['text', 'textarea', 'goal', 'kpi'].includes(localQ.type);

  return (
    <div className="pn-appraisal__q-editor">
      <div className="pn-appraisal__q-editor-header">
        <span className="pn-appraisal__q-editor-header-title">Question Editor</span>
        <button
          className="pn-appraisal__btn pn-appraisal__btn--ghost pn-appraisal__btn--icon"
          onClick={onClose}
          style={{ width: 26, height: 26, color: 'var(--pn-primary)' }}
          aria-label="Close editor"
        >
          <X size={13} />
        </button>
      </div>
      <div className="pn-appraisal__q-editor-body">

        {/* Question Type */}
        <div className="pn-appraisal__form-field" style={{ marginBottom: 14 }}>
          <label className="pn-appraisal__form-label">Question Type</label>
          <div className="pn-appraisal__q-editor-type-grid">
            {QUESTION_TYPES.map((qt) => (
              <button
                key={`qtype-${qt.type}`}
                type="button"
                className={`pn-appraisal__q-editor-type-btn${localQ.type === qt.type ? ' pn-appraisal__q-editor-type-btn--active' : ''}`}
                onClick={() => update({ type: qt.type })}
                aria-pressed={localQ.type === qt.type}
              >
                {qt.icon}
                <span>{qt.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Question Text */}
        <div className="pn-appraisal__form-field" style={{ marginBottom: 14 }}>
          <label className="pn-appraisal__form-label" htmlFor="q-text">
            Question Text <span className="pn-appraisal__form-label-required">*</span>
          </label>
          <textarea
            id="q-text"
            className="pn-appraisal__form-textarea"
            value={localQ.text}
            onChange={(e) => update({ text: e.target.value })}
            rows={2}
            placeholder="Enter your question here…"
          />
        </div>

        {/* Required / Optional */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          <div className="pn-appraisal__form-toggle" style={{ flex: 1 }}>
            <div className="pn-appraisal__form-toggle-info">
              <div className="pn-appraisal__form-toggle-label" style={{ fontSize: 12 }}>Required field</div>
              <div className="pn-appraisal__form-toggle-desc" style={{ fontSize: 11 }}>Employee must answer this question</div>
            </div>
            <button
              type="button"
              className={`pn-appraisal__form-toggle-switch${localQ.required ? ' pn-appraisal__form-toggle-switch--on' : ''}`}
              onClick={() => update({ required: !localQ.required })}
              aria-label="Toggle required"
              role="switch"
              aria-checked={localQ.required}
            />
          </div>
        </div>

        {/* Rating Range */}
        {showRating && (
          <div className="pn-appraisal__form-grid pn-appraisal__form-grid--2" style={{ marginBottom: 14 }}>
            <div className="pn-appraisal__form-field">
              <label className="pn-appraisal__form-label" htmlFor="q-min-rating">Min Rating</label>
              <select
                id="q-min-rating"
                className="pn-appraisal__form-select"
                value={localQ.minRating || 1}
                onChange={(e) => update({ minRating: Number(e.target.value) })}
              >
                {[1, 2, 3].map((n) => <option key={`min-${n}`} value={n}>{n}</option>)}
              </select>
            </div>
            <div className="pn-appraisal__form-field">
              <label className="pn-appraisal__form-label" htmlFor="q-max-rating">Max Rating</label>
              <select
                id="q-max-rating"
                className="pn-appraisal__form-select"
                value={localQ.maxRating || 5}
                onChange={(e) => update({ maxRating: Number(e.target.value) })}
              >
                {[3, 4, 5, 7, 10].map((n) => <option key={`max-${n}`} value={n}>{n}</option>)}
              </select>
            </div>
          </div>
        )}

        {/* Character Limit */}
        {showCharLimit && (
          <div className="pn-appraisal__form-field" style={{ marginBottom: 14 }}>
            <label className="pn-appraisal__form-label" htmlFor="q-char-limit">
              Character Limit
              <span className="pn-appraisal__form-label-hint"> (0 = no limit)</span>
            </label>
            <input
              id="q-char-limit"
              type="number"
              className="pn-appraisal__form-input"
              value={localQ.maxChars || 0}
              onChange={(e) => update({ maxChars: Number(e.target.value) })}
              min={0}
              max={5000}
              step={100}
            />
          </div>
        )}

        {/* Options for select/radio/checkbox */}
        {showOptions && (
          <div className="pn-appraisal__form-field" style={{ marginBottom: 14 }}>
            <label className="pn-appraisal__form-label">Answer Options</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8 }}>
              {(localQ.options || []).map((opt, idx) => (
                <div key={`opt-${idx}`} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{
                    flex: 1, padding: '6px 10px', background: 'var(--pn-surface-2)',
                    border: '1px solid var(--pn-border)', borderRadius: 'var(--pn-radius-sm)',
                    fontSize: 12.5, color: 'var(--pn-text-primary)',
                  }}>
                    {opt}
                  </div>
                  <button
                    className="pn-appraisal__table-actions-btn pn-appraisal__table-actions-btn--danger"
                    onClick={() => removeOption(idx)}
                    style={{ opacity: 1 }}
                    aria-label={`Remove option ${opt}`}
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <input
                className="pn-appraisal__form-input"
                placeholder="Add option…"
                value={optionInput}
                onChange={(e) => setOptionInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addOption()}
                style={{ flex: 1 }}
                aria-label="New option text"
              />
              <button
                className="pn-appraisal__btn pn-appraisal__btn--primary pn-appraisal__btn--sm"
                onClick={addOption}
                disabled={!optionInput.trim()}
              >
                <Plus size={12} /> Add
              </button>
            </div>
            <p className="pn-appraisal__form-hint" style={{ marginTop: 4 }}>
              Press Enter or click Add to insert an option
            </p>
          </div>
        )}

        {/* Conditional Logic */}
        {localQ.type === 'conditional' && (
          <div className="pn-appraisal__form-field" style={{ marginBottom: 14 }}>
            <label className="pn-appraisal__form-label">Conditional Logic</label>
            <div style={{ background: 'var(--pn-warning-light)', border: '1px solid var(--pn-warning-mid)', borderRadius: 'var(--pn-radius)', padding: '10px 12px', fontSize: 12, color: 'var(--pn-warning)', display: 'flex', gap: 8 }}>
              <AlertCircle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>This question will only be shown when a parent question's answer matches a specified value. Configure the parent question and trigger value below.</span>
            </div>
            <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <input
                className="pn-appraisal__form-input"
                placeholder="Parent question ID…"
                value={localQ.conditionalParentId || ''}
                onChange={(e) => update({ conditionalParentId: e.target.value })}
                aria-label="Parent question ID"
              />
              <input
                className="pn-appraisal__form-input"
                placeholder="Trigger value (e.g. 'Yes', '5', 'Above 100%')…"
                value={localQ.conditionalValue || ''}
                onChange={(e) => update({ conditionalValue: e.target.value })}
                aria-label="Conditional trigger value"
              />
            </div>
          </div>
        )}

        {/* Preview of question */}
        <div style={{ background: 'var(--pn-surface-2)', border: '1px solid var(--pn-border)', borderRadius: 'var(--pn-radius)', padding: 12, marginTop: 4 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--pn-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
            Preview
          </div>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--pn-text-primary)', marginBottom: 8 }}>
            {localQ.text}
            {localQ.required && <span style={{ color: 'var(--pn-danger)', marginLeft: 4 }}>*</span>}
          </div>
          {showRating && (
            <div style={{ display: 'flex', gap: 6 }}>
              {Array.from({ length: (localQ.maxRating || 5) - (localQ.minRating || 1) + 1 }, (_, i) => (localQ.minRating || 1) + i).map((n) => (
                <div key={`preview-star-${n}`} style={{
                  width: 32, height: 32, borderRadius: 'var(--pn-radius-sm)', border: '1.5px solid var(--pn-border)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700,
                  color: 'var(--pn-text-secondary)', background: 'var(--pn-surface)',
                }}>
                  {n}
                </div>
              ))}
            </div>
          )}
          {showOptions && localQ.options && localQ.options.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              {localQ.options.map((opt, i) => (
                <div key={`preview-opt-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--pn-text-secondary)' }}>
                  <div style={{ width: 14, height: 14, border: '1.5px solid var(--pn-border-strong)', borderRadius: ['radio', 'single-select'].includes(localQ.type) ? '50%' : 3, flexShrink: 0 }} />
                  {opt}
                </div>
              ))}
            </div>
          )}
          {(localQ.type === 'text' || localQ.type === 'textarea' || localQ.type === 'goal' || localQ.type === 'kpi') && (
            <div style={{
              background: 'var(--pn-surface)', border: '1px solid var(--pn-border)', borderRadius: 'var(--pn-radius-sm)',
              padding: '8px 10px', fontSize: 12, color: 'var(--pn-text-muted)', minHeight: localQ.type === 'textarea' ? 56 : 34,
            }}>
              Enter your response here…
              {localQ.maxChars ? <span style={{ float: 'right', fontSize: 11 }}>0 / {localQ.maxChars}</span> : null}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}